import { Fragment, type ReactNode } from 'react';
import type { LevelDoc } from '../model/format.ts';
import { num } from '../model/unityProps.ts';
import { useEditor } from './store.ts';
import { FPS, SCHEMAS, type FieldSpec } from './schema.ts';
import { fieldAt, ownerRef, refOf, writeField, writeList, type CompAddr, type CompRef } from './components.ts';
import { controlledSlot, entityNamer, slotRefs, toggleLink, setSingleLink, writeControlled, type Accept, type LinkSlot } from './links.ts';
import { Check, ColorInput, Icon, NumberInput, Row, Select, TextInput } from './widgets.tsx';

const CONTROL_AXES: [number, string][] = [[0, 'Not moved'], [1, 'Left stick X'], [2, 'Left stick Y'], [3, 'Right stick X'], [4, 'Right stick Y']];

const sameSlot = (a: LinkSlot | null, b: LinkSlot) => !!a && a.path === b.path && JSON.stringify(a.addr) === JSON.stringify(b.addr);

/** Select the entity a ref points at. */
export function selectEntity(ref: string) {
  const [ent] = ref.split('#');
  const [kind, id] = ent.split(':');
  const st = useEditor.getState();
  if (kind === 'obj') st.set({ selection: { kind: 'objects', ids: [id] } });
  else if (kind === 'body') st.set({ selection: { kind: 'body', id } });
}

export function startPick(slot: LinkSlot) {
  const st = useEditor.getState();
  if (sameSlot(st.linkSlot, slot)) {
    st.set({ linkSlot: null, tool: 'select' });
    return;
  }
  st.set({ tool: 'link', linkSlot: slot });
  st.notify(slot.accept === 'body' ? 'Tap a moving platform or block to control it' : slot.accept === 'panel' ? 'Tap a control panel to link it' : slot.list ? 'Tap activators to link or unlink them' : 'Tap an activator to link it');
}

function PickButton({ slot, label = 'Pick' }: { slot: LinkSlot; label?: string }) {
  const active = useEditor((s) => sameSlot(s.linkSlot, slot) && s.tool === 'link');
  return (
    <button className={`mini pick ${active ? 'active' : ''}`} title="Pick on the canvas" onClick={(e) => { e.preventDefault(); startPick(slot); }}>
      <Icon name="link" size={12} /> {active ? 'Done' : label}
    </button>
  );
}

function RefName({ doc, r }: { doc: LevelDoc; r: string }) {
  const name = entityNamer(doc);
  return (
    <button className="ref linkish" title="Select it" onClick={(e) => { e.preventDefault(); selectEntity(r); }}>
      {name(r)}
    </button>
  );
}

/** Single reference field (e.g. an ActivatorBase `activator`). */
export function LinkField({ doc, addr, path, label, hint, raw, accept = 'activator' }: { doc: LevelDoc; addr: CompAddr; path: string; label: string; hint?: string; raw: any; accept?: Accept }) {
  const slot: LinkSlot = { addr, path, list: false, accept, label };
  const r = refOf(raw);
  const internal = !r && raw && typeof raw === 'object' && String(raw.fileID ?? '0') !== '0';
  return (
    <div className="row" title={hint}>
      <span className="row-label">{label}</span>
      <span className="row-value link-value">
        {r ? <RefName doc={doc} r={r} /> : <span className="muted small">{internal ? 'Built into the prefab' : hint?.startsWith('Empty') ? hint.replace('Empty: ', '') : 'None'}</span>}
        {(r || internal) && <button className="mini" title="Unlink" onClick={(e) => { e.preventDefault(); setSingleLink(slot, null); }}>×</button>}
        <PickButton slot={slot} />
      </span>
    </div>
  );
}

/** Reference list (`activators`, control panels). */
export function LinkList({ doc, slot, hint }: { doc: LevelDoc; slot: LinkSlot; hint?: string }) {
  const refs = slotRefs(doc, slot);
  return (
    <div className="link-list" title={hint}>
      <div className="row">
        <span className="row-label">{slot.label.split(': ').pop()}</span>
        <span className="row-value link-value">
          {!refs.length && <span className="muted small">{hint?.startsWith('Empty') ? hint.replace('Empty: ', '') : 'None'}</span>}
          <PickButton slot={slot} label="Add" />
        </span>
      </div>
      {refs.map((r) => (
        <div key={r} className="row sub">
          <span className="row-label" />
          <span className="row-value link-value">
            <RefName doc={doc} r={r} />
            <button className="mini" title="Unlink" onClick={(e) => { e.preventDefault(); toggleLink(slot, r); }}>×</button>
          </span>
        </div>
      ))}
    </div>
  );
}

function secondsOf(v: any): number {
  if (v && typeof v === 'object') {
    if (v._frames !== undefined && v._frames !== '') return num(v._frames) / FPS;
    return num(v._seconds);
  }
  return 0;
}

function writeSeconds(addr: CompAddr, path: string, label: string, s: number) {
  useEditor.getState().change(label, (d) => {
    writeField(d, addr, `${path}._seconds`, { v: String(+s.toFixed(4)) });
    writeField(d, addr, `${path}._frames`, { v: String(Math.round(s * FPS)) });
  });
}

/** One curated field of a component. */
function Field({ doc, comp, f, title }: { doc: LevelDoc; comp: CompRef; f: FieldSpec; title: string }) {
  const change = useEditor((s) => s.change);
  const raw = fieldAt(comp.data, f.p);
  const str = raw === undefined || raw === null || typeof raw === 'object' ? f.def : String(raw);
  const set = (v: string) => change(`${title}: ${f.label}`, (d) => writeField(d, comp.addr, f.p, { v }));
  const row = (input: ReactNode) => (
    <Row label={f.label} title={f.hint}>
      {input}
    </Row>
  );
  const k = f.kind;
  switch (k.k) {
    case 'num': {
      const scale = k.scale ?? 1;
      return row(
        <span className="inline">
          <NumberInput value={+(num(str) * scale).toFixed(4)} step={k.step} min={k.min} max={k.max} onChange={(v) => set(String(+(v / scale).toPrecision(7)))} />
          {k.unit && <span className="muted small">{k.unit}</span>}
        </span>,
      );
    }
    case 'int':
      return row(<NumberInput value={Math.round(num(str))} step={1} min={k.min} max={k.max} onChange={(v) => set(String(Math.round(v)))} />);
    case 'bool': {
      const on = (str ?? '0') === '1';
      return row(<Check value={k.invert ? !on : on} onChange={(v) => set((k.invert ? !v : v) ? '1' : '0')} />);
    }
    case 'enum': {
      const v = num(str, k.options[0][0]);
      const opts = k.options.some(([n]) => n === v) ? k.options : [...k.options, [v, `Value ${v}`] as [number, string]];
      return row(<Select value={v} options={opts} onChange={(n) => set(String(n))} />);
    }
    case 'text':
      return row(<TextInput value={str ?? ''} placeholder={k.placeholder} onChange={set} />);
    case 'seconds':
      return row(
        <span className="inline">
          <NumberInput value={+secondsOf(raw).toFixed(3)} step={0.1} min={0} onChange={(v) => writeSeconds(comp.addr, f.p, `${title}: ${f.label}`, v)} />
          <span className="muted small">s</span>
        </span>,
      );
    case 'color': {
      const c: [number, number, number, number] = [num(raw?.r, 1), num(raw?.g, 1), num(raw?.b, 1), num(raw?.a, 1)];
      return row(
        <ColorInput
          value={c}
          onChange={(n) =>
            change(`${title}: ${f.label}`, (d) => (['r', 'g', 'b', 'a'] as const).forEach((ch, i) => writeField(d, comp.addr, `${f.p}.${ch}`, { v: String(+n[i].toFixed(4)) })))
          }
        />,
      );
    }
    case 'vec2':
      return row(
        <span className="vec">
          {(['x', 'y'] as const).map((ax) => (
            <NumberInput key={ax} value={num(raw?.[ax])} step={k.step ?? 0.5} width={64} onChange={(v) => change(`${title}: ${f.label}`, (d) => writeField(d, comp.addr, `${f.p}.${ax}`, { v: String(v) }))} />
          ))}
        </span>,
      );
    case 'activator':
      return <LinkField doc={doc} addr={comp.addr} path={f.p} label={f.label} hint={f.hint} raw={raw} />;
    case 'activators':
    case 'panels':
      return <LinkList doc={doc} hint={f.hint} slot={{ addr: comp.addr, path: f.p, list: true, accept: k.k === 'panels' ? 'panel' : 'activator', label: `${title}: ${f.label}` }} />;
    case 'rotatePoints':
      return <RotatePoints comp={comp} path={f.p} />;
    case 'controlled':
      return <ControlledBodies doc={doc} comp={comp} />;
    case 'opt':
      return <OptionalField comp={comp} f={f} title={title} raw={raw} />;
  }
}

/** SN<T> override: the first checkbox applies the value, which otherwise stays at the prefab default. */
function OptionalField({ comp, f, title, raw }: { comp: CompRef; f: FieldSpec; title: string; raw: unknown }) {
  const change = useEditor((s) => s.change);
  const k = f.kind;
  if (k.k !== 'opt') return null;
  const on = !!raw && typeof raw === 'object' && String((raw as { hasValue?: unknown }).hasValue ?? '0') === '1';
  const def = f.def ?? '0';
  const setOn = (next: boolean) =>
    change(`${title}: ${f.label}`, (d) => {
      writeField(d, comp.addr, `${f.p}.hasValue`, { v: next ? '1' : '0' });
      if (next) writeField(d, comp.addr, `${f.p}.v`, { v: def });
    });
  const setV = (v: string) => change(`${title}: ${f.label}`, (d) => writeField(d, comp.addr, `${f.p}.v`, { v }));
  const shown = on && raw && typeof raw === 'object' && (raw as { v?: unknown }).v !== undefined && (raw as { v?: unknown }).v !== '' ? String((raw as { v?: unknown }).v) : def;
  let value: ReactNode;
  if (k.of === 'bool') {
    value = <Check value={shown === '1'} disabled={!on} onChange={(v) => setV(v ? '1' : '0')} />;
  } else if (k.of === 'int') {
    value = <NumberInput value={Math.round(num(shown))} step={1} min={k.min} max={k.max} disabled={!on} onChange={(v) => setV(String(Math.round(v)))} />;
  } else {
    value = <NumberInput value={+num(shown).toFixed(4)} step={k.step} min={k.min} max={k.max} disabled={!on} onChange={(v) => setV(String(+v.toPrecision(7)))} />;
  }
  return (
    <Row label={f.label} title={f.hint ?? 'Check to use a custom value. Unchecked keeps the player prefab.'} className={on ? 'overridden' : undefined}>
      <span className="inline">
        <span title="Use a custom value in this level"><Check value={on} onChange={setOn} /></span>
        {value}
      </span>
    </Row>
  );
}

/** Curated fields of a component (see schema.ts). */
export function CompFields({ doc, comp }: { doc: LevelDoc; comp: CompRef }) {
  const schema = SCHEMAS[comp.type];
  if (!schema) return null;
  return (
    <div className="fields">
      {schema.fields.map((f, i) => (
        <Fragment key={f.p}>
          {f.group && f.group !== schema.fields[i - 1]?.group && <div className="field-group">{f.group}</div>}
          <Field doc={doc} comp={comp} f={f} title={schema.title} />
        </Fragment>
      ))}
    </div>
  );
}

function RotatePoints({ comp, path }: { comp: CompRef; path: string }) {
  const change = useEditor((s) => s.change);
  const pts: any[] = Array.isArray(comp.data?.[path]) ? comp.data[path] : [];
  const write = (label: string, next: any[]) =>
    change(label, (d) =>
      writeList(
        d,
        comp.addr,
        path,
        next.map((p) => ({
          endAngle: String(num(p.endAngle)),
          rotateSpeed: String(num(p.rotateSpeed)),
          acceleration: String(num(p.acceleration)),
          'pauseTime._seconds': String(+secondsOf(p.pauseTime).toFixed(4)),
          'pauseTime._frames': String(Math.round(secondsOf(p.pauseTime) * FPS)),
          'pauseTime.counter': '0',
        })),
      ),
    );
  const edit = (i: number, patch: Record<string, any>) => write('Edit turn angle', pts.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <div className="sub-list">
      <div className="row">
        <span className="row-label">Turn angles</span>
        <span className="row-value muted small">{pts.length ? 'turns to each angle in order' : 'none: spins continuously'}</span>
      </div>
      {pts.map((p, i) => (
        <div key={i} className="sub-item">
          <div className="sub-item-head">
            <b>#{i}</b>
            <button className="mini" title="Remove" onClick={() => write('Remove turn angle', pts.filter((_, j) => j !== i))}>×</button>
          </div>
          <Row label="Angle (°)"><NumberInput value={num(p.endAngle)} step={15} onChange={(v) => edit(i, { endAngle: v })} /></Row>
          <Row label="Pause (s)"><NumberInput value={+secondsOf(p.pauseTime).toFixed(3)} step={0.1} min={0} onChange={(v) => edit(i, { pauseTime: { _seconds: v, _frames: Math.round(v * FPS) } })} /></Row>
          <Row label="Speed (°/s)" title="0 = use the rotation speed above">
            <NumberInput value={+(num(p.rotateSpeed) * FPS).toFixed(3)} step={30} min={0} onChange={(v) => edit(i, { rotateSpeed: +(v / FPS).toPrecision(7) })} />
          </Row>
          <Row label="Accel (°/s²)" title="0 = use the acceleration above">
            <NumberInput value={+(num(p.acceleration) * FPS * FPS).toFixed(2)} step={100} min={0} onChange={(v) => edit(i, { acceleration: +(v / FPS / FPS).toPrecision(7) })} />
          </Row>
        </div>
      ))}
      <button
        className="btn small-btn"
        onClick={() => {
          const last = pts[pts.length - 1];
          write('Add turn angle', [...pts, { endAngle: (num(last?.endAngle) + 90) % 360, rotateSpeed: 0, acceleration: 0, pauseTime: { _seconds: secondsOf(last?.pauseTime) || 1 } }]);
        }}
      >
        <Icon name="plus" size={14} /> Angle
      </button>
    </div>
  );
}

function ControlledBodies({ doc, comp }: { doc: LevelDoc; comp: CompRef }) {
  const change = useEditor((s) => s.change);
  const items: any[] = Array.isArray(comp.data?.uiRigidBodiesOrGameObjects) ? comp.data.uiRigidBodiesOrGameObjects : [];
  const slot = controlledSlot(comp.addr);
  const edit = (i: number, patch: Record<string, string>) => change('Edit controlled body', (d) => writeControlled(d, comp.addr, items.map((it, j) => (j === i ? { ...it, ...patch } : it))));
  return (
    <div className="sub-list">
      <div className="row">
        <span className="row-label">Controlled bodies</span>
        <span className="row-value link-value">
          {!items.length && <span className="muted small">None</span>}
          <PickButton slot={slot} label="Add" />
        </span>
      </div>
      {items.map((it, i) => {
        const r = refOf(it?.rigidbodyOrParent);
        return (
          <div key={i} className="sub-item">
            <div className="sub-item-head">
              {r ? <RefName doc={doc} r={r} /> : <span className="muted small">{it?.rigidbodyOrParent?.fileID && it.rigidbodyOrParent.fileID !== '0' ? 'Built into the prefab' : 'Nothing'}</span>}
              <button className="mini" title="Remove" onClick={() => change('Remove controlled body', (d) => writeControlled(d, comp.addr, items.filter((_, j) => j !== i)))}>×</button>
            </div>
            <Row label="Horizontal from"><Select value={num(it?.xAxisControls)} options={CONTROL_AXES} onChange={(v) => edit(i, { xAxisControls: String(v) })} /></Row>
            <Row label="Horizontal scale"><NumberInput value={num(it?.xInputAxisScale, 1)} step={0.5} onChange={(v) => edit(i, { xInputAxisScale: String(v) })} /></Row>
            <Row label="Vertical from"><Select value={num(it?.yAxisControls)} options={CONTROL_AXES} onChange={(v) => edit(i, { yAxisControls: String(v) })} /></Row>
            <Row label="Vertical scale"><NumberInput value={num(it?.yInputAxisScale, 1)} step={0.5} onChange={(v) => edit(i, { yInputAxisScale: String(v) })} /></Row>
          </div>
        );
      })}
    </div>
  );
}

/** Controllers (BodyControlHandlers) steering a body, edited from the body's side. */
export function ControllerRows({ doc, rows }: { doc: LevelDoc; rows: { addr: CompAddr; index: number; item: any }[] }) {
  const change = useEditor((s) => s.change);
  return (
    <>
      {rows.map(({ addr, index, item }) => {
        const owner = ownerRef(addr);
        const path = `uiRigidBodiesOrGameObjects.Array.data[${index}]`;
        const set = (k: string, v: string) => change('Edit controller', (d) => writeField(d, addr, `${path}.${k}`, { v }));
        return (
          <div key={owner + index} className="sub-item">
            <div className="sub-item-head">
              <RefName doc={doc} r={owner} />
            </div>
            <Row label="Horizontal from"><Select value={num(item?.xAxisControls)} options={CONTROL_AXES} onChange={(v) => set('xAxisControls', String(v))} /></Row>
            <Row label="Vertical from"><Select value={num(item?.yAxisControls)} options={CONTROL_AXES} onChange={(v) => set('yAxisControls', String(v))} /></Row>
          </div>
        );
      })}
    </>
  );
}
